import React, { useEffect, useState } from 'react';
import { ProductSuggestionsContainer } from '../../ProductSuggestionsContainer';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { AlternativesProduct } from '../AlternativesProduct';
import { Slideout } from '../../Slideout/Slideout';
import { styles } from './AlternativesSlideout.css';
import { useProductActionsContext } from '../../context/ProductActionsProvider';
import { useAlternativesContext } from '../AlternativesContextProvider';
import { AddedToCartAlert } from '../../AddedToCartAlert';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner';

export const ANIMATION_DURATION = 3000;

export const AlternativesSlideout = () => {
  const isMobile = useBreakpoint('md', 'down');
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [shouldDisplayCartActions, setShouldDisplayCartActions] = useState(false);
  const {
    alternatives,
    isSlideoutOpen,
    currentProduct,
    isLoading,
    handleCloseSlideout,
    handleReviewCartClick,
    hanleContinueShoppingClick,
  } = useAlternativesContext();

  const { onAddToCart } = useProductActionsContext();
  const { slideoutType } = useAlternativesContext();

  const isAlternativesSlideout = slideoutType === 'alternatives';
  const title = isAlternativesSlideout ? 'Browse alternatives' : 'Similar items';

  const handleAddToCart = async (e: React.SyntheticEvent, hit: AlgoliaProductHit) => {
    if (!onAddToCart || isAddedToCart) return false;
    const isSuccess = await onAddToCart(e, hit, hit.variant_id, undefined, 'alternatives');
    if (isSuccess) {
      setShouldDisplayCartActions(true);
      setIsAddedToCart(true);
    }
    return isSuccess;
  };

  useEffect(() => {
    if (!isAddedToCart) return;
    const timer = setTimeout(() => setIsAddedToCart(false), ANIMATION_DURATION);

    return () => clearTimeout(timer);
  }, [isAddedToCart]);

  const onClose = () => {
    handleCloseSlideout();
    setIsAddedToCart(false);
  };

  return (
    <Slideout
      backgroundColor={isMobile ? 'white' : 'grey'}
      isOpen={isSlideoutOpen}
      onClose={onClose}
      size="23.438rem"
      anchor="right"
      dataTestId="alternatives-slideout"
    >
      {currentProduct && isMobile ? (
        <AlternativesProduct
          {...currentProduct}
          productImage={currentProduct.product_image}
          onAddToCart={(e: React.SyntheticEvent) => handleAddToCart(e, currentProduct)}
        />
      ) : null}
      <div className={styles.container}>
        <h3 className={styles.heading}>{title}</h3>
        <div className={styles.content}>
          {isLoading ? <Spinner /> : null}
          {!isLoading && alternatives.length ? (
            <ProductSuggestionsContainer
              tileVariant={isAlternativesSlideout ? 'minified' : 'fullWidth'}
              suggestions={alternatives}
              onAddToCart={handleAddToCart}
              type="alternatives"
            />
          ) : null}
          {!isLoading && !alternatives.length ? (
            <p className={styles.noResultsHeader}>Looks like this item is one of a kind.</p>
          ) : null}
        </div>
      </div>
      {shouldDisplayCartActions ? (
        <div className={styles.addedToCartAlert}>
          <div className={styles.addedToCartAlertBackground} />
          <AddedToCartAlert
            onContinueShoppingClick={hanleContinueShoppingClick}
            onReviewCartClick={handleReviewCartClick}
            variant="minimalist"
            background="none"
            isAddedToCart={isAddedToCart}
            shouldPlayAddedAnimation
          />
        </div>
      ) : null}
    </Slideout>
  );
};
